
import { useEffect } from "react";
import { useSelector } from "react-redux";
import {
  addReplaceHistory,
  replaceAll,
  setAllMatches,
  setCurrentMatchIndex,
  updateBlock,
  updateText,
} from "../redux/JobModule/QCDetailPage/QCDetailPageAction";
import { RootState } from "../redux/store";
import { useAppDispatch } from "../redux/useAppDispatch";
import { scrollIntoView } from "../utils/scrollIntoView";
import { TranscriptionBlockType } from "../utils/type";
import { eliminateLinesAndSymbols } from "../utils/eliminateInvalidLinesInDictationFile";


export interface Match {
  textIndex: number;
  start: number;
  end: number;
}
//  Escape regex
const escapeRegExp = (text: string) => {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};
const useFindReplace = (
  texts: TranscriptionBlockType[],
  type?: string,
  editorRef?: React.MutableRefObject<any>
) => {
  const allMatches = useSelector((state: RootState) => state.qCDetailPageState.allMatches);
  const currentMatchIndex = useSelector((state: RootState) => state.qCDetailPageState.currentMatchIndex);
  const transcriptionId = useSelector((state: RootState) => state.qCDetailPageState.data.transcriptions[0].idTranscription);
  const replaceText = useSelector((state: RootState) => state.qCDetailPageState.replaceText);
  const searchQuery = useSelector((state: RootState) => state.qCDetailPageState.searchQuery);
  const isCCActive = useSelector((state: RootState) => state.qCDetailPageState.isCCActive);
  const isFullWordActive = useSelector((state: RootState) => state.qCDetailPageState.isFullWordActive);
  const dispatch = useAppDispatch();
  const setAllMatchesValue = (matches: Match[]) => dispatch(setAllMatches(matches));
  const setCurrentMatchIndexValue = (index: number) => dispatch(setCurrentMatchIndex(index));
  const updateTranscriptText = (newTexts: TranscriptionBlockType[]) => dispatch(updateText(newTexts));
  // FIND
  const findMatches = (query: string) => {
    let matches: Match[] = [];
    if (!query?.trim()) {
      setAllMatchesValue([]);
      setCurrentMatchIndexValue(0);
      return;
    }
    const safeQuery = escapeRegExp(query);
    if (type === "Dictation") {
      let text = eliminateLinesAndSymbols(
        texts[0]?.transcript || ""
      );
      const flags = isCCActive ? "g" : "gi";
      const pattern = isFullWordActive ? `\\b${safeQuery}\\b` : safeQuery;
      const re = new RegExp(pattern, flags);
      let match;
      while ((match = re.exec(text)) !== null) {
        matches.push({
          textIndex: 0,
          start: match.index,
          end: match.index + match[0].length,
        });
      }
    } else {
      texts.forEach((text, textIndex) => {
        const flags = isCCActive ? "g" : "gi";
        const pattern = isFullWordActive ? `\\b${safeQuery}\\b` : safeQuery;
        const re = new RegExp(pattern, flags);
        let match;
        while ((match = re.exec(text.transcript)) !== null) {
          matches.push({
            textIndex,
            start: match.index,
            end: match.index + match[0].length,
          });
        }
      });
    }
    setAllMatchesValue(matches);
    setCurrentMatchIndexValue(0);
  };
  //  NAVIGATION
  const handleNext = () => {
    if (!allMatches.length) return;
    const nextIndex = (currentMatchIndex + 1) % allMatches.length;
    scrollIntoView(allMatches[nextIndex].textIndex, false);
    setCurrentMatchIndexValue(nextIndex);
  };
  const handlePrev = () => {
    if (!allMatches.length) return;
    const prevIndex = (currentMatchIndex - 1 + allMatches.length) % allMatches.length;
    scrollIntoView(allMatches[prevIndex].textIndex, false);
    setCurrentMatchIndexValue(prevIndex);
  };
  // REPLACE
    const handleReplace = async () => {
      if (!allMatches.length) return;

      const { textIndex, start, end } = allMatches[currentMatchIndex];

      if (type === "Dictation") {
        let text = eliminateLinesAndSymbols(
          texts[0]?.transcript || ""
        );

        const updated =
          text.substring(0, start) +
          replaceText +
          text.substring(end);

        // update UI
        await editorRef?.current?.setMarkdown(updated);

        // update redux
        const updatedTexts = [...texts];
        updatedTexts[0] = {
          ...updatedTexts[0],
          transcript: updated,
        };
        updateTranscriptText(updatedTexts);
      } else {
        const text = texts[textIndex]?.transcript || "";

        const updated =
          text.substring(0, start) +
          replaceText +
          text.substring(end);

        const updatedTexts = [...texts];
        updatedTexts[textIndex] = {
          ...updatedTexts[textIndex],
          transcript: updated,
        };
        updateTranscriptText(updatedTexts);
      }

      dispatch(addReplaceHistory(replaceText));

      findMatches(searchQuery);
    };
  //  REPLACE ALL
  const handleReplaceAll = async () => {
    const safeQuery = escapeRegExp(searchQuery);
    const flags = isCCActive ? "g" : "gi";
    const pattern = isFullWordActive
      ? `\\b${safeQuery}\\b`
      : safeQuery;

    const re = new RegExp(pattern, flags);

    if (type === "Dictation") {
      let text = eliminateLinesAndSymbols(
        texts[0]?.transcript || ""
      );

      const updated = text.replace(re, replaceText);

      await editorRef?.current?.setMarkdown(updated);

      const updatedTexts = [...texts];
      updatedTexts[0] = {
        ...updatedTexts[0],
        transcript: updated,
      };
      updateTranscriptText(updatedTexts);
    } else {
      const updatedTexts = texts.map((t) => ({
        ...t,
        transcript: t.transcript.replace(re, replaceText),
      }));
      updateTranscriptText(updatedTexts);
    }

    dispatch(addReplaceHistory(replaceText));

    findMatches(searchQuery);
  };
  useEffect(() => {
    findMatches(searchQuery);
  }, [texts, isCCActive, isFullWordActive]);
  return {
    searchQuery,
    replaceText,
    texts,
    updateTranscriptText,
    allMatches,
    currentMatchIndex,
    handleNext,
    handlePrev,
    handleReplace,
    handleReplaceAll,
    findMatches,
  };
};
export default useFindReplace;
